import { useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { AttendanceFormSessionDetailsActions, selectAttendanceFormSessionDetails } from "./state";
import { useLazyQuery } from "@apollo/client/react";
import { GET_ATTENDANCE_FORM_DATA } from "AttendanceForm/Utils/Queries/AttendanceFormValidateSession";
import * as AttendanceFormTypes from "AttendanceForm/Utils/GraphQl/AttendanceFormData";
import { SessionDetails } from "AttendanceForm/Utils/GraphQl/AttendanceFormData";

interface AttendanceFormSessionDetailsHook {
  loading: boolean;
  fetched: boolean;
  fetchAttendanceFormSessionDetails: () => void;
  session: SessionDetails;
}

export const useAttendanceFormSessionDetails = (
  eventId: string,
  eventHash: string,
): AttendanceFormSessionDetailsHook => {
  const { fetched, session } = useSelector(selectAttendanceFormSessionDetails);

  const dispatch = useDispatch();

  const [getAttendanceFormSessionDetails, { loading, data }] =
    useLazyQuery<AttendanceFormTypes.AttendanceForm>(GET_ATTENDANCE_FORM_DATA, {
      fetchPolicy: "no-cache",
    });

  useEffect(() => {
    if (data) {
      const sessionDetails = {
        sessionId: data.attendanceFormData.sessionDetails.sessionId,
        sessionName: data.attendanceFormData.sessionDetails.sessionName,
        externalSessionId: data.attendanceFormData.sessionDetails.externalSessionId,
        surveyId: data.attendanceFormData.sessionDetails.surveyId,
        startDate: data.attendanceFormData.sessionDetails.startDate,
        endDate: data.attendanceFormData.sessionDetails.endDate,
        programmeId: data.attendanceFormData.sessionDetails.programmeId,
        programmeName: data.attendanceFormData.sessionDetails.programmeName,
        activityName: data.attendanceFormData.sessionDetails.activityName,
        activityType: data.attendanceFormData.sessionDetails.activityType,
        activityId: data.attendanceFormData.sessionDetails.activityId,
      } as SessionDetails;
      dispatch(
        AttendanceFormSessionDetailsActions.fetchAttendanceFormSessionDetailsSuccess(
          sessionDetails,
        ),
      );
    }
  }, [data, dispatch]);

  const fetchAttendanceFormSessionDetails = useCallback(async () => {
    try {
      await getAttendanceFormSessionDetails({
        variables: { eventId: eventId, eventIdHash: eventHash },
      });
    } catch (error) {
      // Ignore AbortError - expected in Apollo Client 3.x when component unmounts during fetch
      if (error instanceof Error && error.name === "AbortError") {
        return;
      }
      throw error;
    }
  }, [getAttendanceFormSessionDetails, eventId, eventHash]);

  return {
    loading,
    fetchAttendanceFormSessionDetails,
    fetched,
    session,
  };
};
